import { Alert, Button, Divider, Drawer, Group, Select, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import type { IOrderStoreTransferPreview } from 'washday-sdk';
import { useGetCashierboxes } from '../../hooks/cashierboxes/queries';
import { usePreviewOrderStoreTransfer, useTransferOrderStore } from '../../hooks/orders/mutations';
import { useGetStores } from '../../hooks/stores/queries';
import { IOrder } from '../../interfaces/Order';
import { showNotification } from '../../utils/Notification/Notification';

type OrderStoreTransferDrawerProps = {
  opened: boolean;
  onClose: () => void;
  order: IOrder;
};

type BackendError = {
  response?: {
    data?: {
      data?: unknown;
    };
  };
};

const isStoreTransferPreview = (value: unknown): value is IOrderStoreTransferPreview => {
  if (!value || typeof value !== 'object') return false;
  const preview = value as Partial<IOrderStoreTransferPreview>;
  return (
    typeof preview.canTransfer === 'boolean' &&
    Array.isArray(preview.blockers) &&
    Array.isArray(preview.warnings)
  );
};

const getStoreId = (store: IOrder['createdIn']) =>
  typeof store === 'string' ? store : store?._id || '';

const OrderStoreTransferDrawer = ({ opened, onClose, order }: OrderStoreTransferDrawerProps) => {
  const { t } = useTranslation(['CommonButtons', 'OrdersCardMoreActionMenu']);
  const history = useHistory();
  const storesQuery = useGetStores(true, opened);
  const previewTransfer = usePreviewOrderStoreTransfer();
  const transferOrder = useTransferOrderStore();
  const [targetStoreId, setTargetStoreId] = useState<string | null>(null);
  const [targetCashierBoxId, setTargetCashierBoxId] = useState<string | null>(null);
  const [preview, setPreview] = useState<IOrderStoreTransferPreview | null>(null);
  const [error, setError] = useState('');
  const sourceStoreId = getStoreId(order.createdIn);
  const isPending = previewTransfer.isPending || transferOrder.isPending;
  const paymentTransferRequired = preview?.info?.paymentTransfer?.required === true;
  const targetCashierBoxesQuery = useGetCashierboxes(
    paymentTransferRequired,
    paymentTransferRequired ? preview?.targetStore._id || '' : ''
  );

  const destinationOptions = useMemo(
    () =>
      (storesQuery.data || [])
        .filter((store) => store.isActive !== false && store._id && store._id !== sourceStoreId)
        .map((store) => ({ value: store._id || '', label: store.name || '' })),
    [sourceStoreId, storesQuery.data]
  );
  const targetCashierBoxOptions = useMemo(
    () =>
      (targetCashierBoxesQuery.data || [])
        .filter((cashierBox) => cashierBox.isActive !== false && cashierBox._id)
        .map((cashierBox) => ({ value: cashierBox._id || '', label: cashierBox.name || '' })),
    [targetCashierBoxesQuery.data]
  );

  useEffect(() => {
    if (!opened) return;
    setTargetStoreId(null);
    setTargetCashierBoxId(null);
    setPreview(null);
    setError('');
  }, [opened, order._id]);

  const getErrorMessage = () => t('OrdersCardMoreActionMenu:storeTransfer.genericError');

  const getTransferIssueMessage = (issue: { code: string; message: string }) =>
    t(`OrdersCardMoreActionMenu:storeTransfer.issues.${issue.code}`, {
      defaultValue: issue.message
    });

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleDestinationChange = (value: string | null) => {
    setTargetStoreId(value);
    setTargetCashierBoxId(null);
    setPreview(null);
    setError('');
  };

  const handlePreview = async () => {
    if (!order._id || !targetStoreId || previewTransfer.isPending) return;

    try {
      setError('');
      const nextPreview = await previewTransfer.mutateAsync({
        orderId: order._id,
        targetStoreId
      });
      setPreview(nextPreview);
    } catch {
      setError(getErrorMessage());
    }
  };

  const handleTransfer = async () => {
    const hasRequiredCashierBox =
      !paymentTransferRequired ||
      Boolean(
        targetCashierBoxId &&
          targetCashierBoxOptions.some((option) => option.value === targetCashierBoxId)
      );
    if (
      !order._id ||
      !targetStoreId ||
      !preview ||
      !hasRequiredCashierBox ||
      transferOrder.isPending
    ) {
      return;
    }

    try {
      setError('');
      const success = await transferOrder.mutateAsync({
        orderId: order._id,
        targetStoreId,
        ...(paymentTransferRequired && targetCashierBoxId ? { targetCashierBoxId } : {}),
        acknowledgedWarnings: preview.warnings.length > 0
      });
      showNotification({
        type: 'Success',
        title: t('OrdersCardMoreActionMenu:storeTransfer.success', {
          folio: success.newFolio
        })
      });
      onClose();

      const params = new URLSearchParams(window.location.search);
      params.set('storeId', success.targetStoreId);
      history.replace({
        pathname: window.location.pathname,
        search: params.toString()
      });
    } catch (transferError) {
      const latestPreview = (transferError as BackendError).response?.data?.data;
      if (isStoreTransferPreview(latestPreview)) {
        setPreview(latestPreview);
        setError('');
        return;
      }
      setError(getErrorMessage());
    }
  };

  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const canConfirm = Boolean(preview?.canTransfer && blockers.length === 0);
  const cashierBoxSelectionReady =
    !paymentTransferRequired ||
    Boolean(
      targetCashierBoxId &&
        targetCashierBoxOptions.some((option) => option.value === targetCashierBoxId)
    );

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="lg"
      title={t('OrdersCardMoreActionMenu:storeTransfer.title')}
      withCloseButton={!isPending}
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
    >
      <Stack gap="md">
        <Select
          label={t('OrdersCardMoreActionMenu:storeTransfer.destinationLabel')}
          placeholder={t('OrdersCardMoreActionMenu:storeTransfer.destinationPlaceholder')}
          data={destinationOptions}
          value={targetStoreId}
          onChange={handleDestinationChange}
          searchable
          disabled={isPending || storesQuery.isLoading}
        />

        {destinationOptions.length === 0 && !storesQuery.isLoading && !storesQuery.isError ? (
          <Alert color="yellow">
            {t('OrdersCardMoreActionMenu:storeTransfer.noDestinationStores')}
          </Alert>
        ) : null}

        {storesQuery.isError ? (
          <Alert color="red">{t('OrdersCardMoreActionMenu:storeTransfer.genericError')}</Alert>
        ) : null}

        {error ? <Alert color="red">{error}</Alert> : null}

        {blockers.length > 0 || preview?.canTransfer === false ? (
          <Alert color="red" title={t('OrdersCardMoreActionMenu:storeTransfer.blockedTitle')}>
            <ul>
              {blockers.map((blocker) => (
                <li key={`${blocker.code}-${blocker.message}`}>
                  {getTransferIssueMessage(blocker)}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {(preview?.info?.outsourcedOrdersCount || 0) > 0 ? (
          <Alert color="blue">
            {t('OrdersCardMoreActionMenu:storeTransfer.outsourcingInfo', {
              count: preview?.info.outsourcedOrdersCount,
              storeName: preview?.targetStore.name
            })}
          </Alert>
        ) : null}

        {canConfirm ? (
          <>
            <Divider />

            {paymentTransferRequired ? (
              <Stack gap="xs">
                <Alert color="blue">
                  {t('OrdersCardMoreActionMenu:storeTransfer.paymentTransferDescription')}
                </Alert>
                <Select
                  label={t('OrdersCardMoreActionMenu:storeTransfer.cashierBoxLabel')}
                  placeholder={t('OrdersCardMoreActionMenu:storeTransfer.cashierBoxPlaceholder')}
                  data={targetCashierBoxOptions}
                  value={targetCashierBoxId}
                  onChange={setTargetCashierBoxId}
                  searchable
                  disabled={
                    isPending ||
                    targetCashierBoxesQuery.isLoading ||
                    targetCashierBoxesQuery.isError ||
                    targetCashierBoxOptions.length === 0
                  }
                />
                {targetCashierBoxesQuery.isError ? (
                  <Alert color="red">
                    {t('OrdersCardMoreActionMenu:storeTransfer.cashierBoxesError')}
                  </Alert>
                ) : null}
                {!targetCashierBoxesQuery.isLoading &&
                !targetCashierBoxesQuery.isError &&
                targetCashierBoxOptions.length === 0 ? (
                  <Alert color="yellow">
                    {t('OrdersCardMoreActionMenu:storeTransfer.noCashierBoxes')}
                  </Alert>
                ) : null}
              </Stack>
            ) : null}

            <Alert
              color="yellow"
              variant="light"
              title={t('OrdersCardMoreActionMenu:storeTransfer.confirmationTitle')}
            >
              <Stack gap="xs">
                <Text size="sm">
                  {t('OrdersCardMoreActionMenu:storeTransfer.confirmationDescription', {
                    storeName: preview?.targetStore.name
                  })}
                </Text>
                <Text size="sm">
                  {t('OrdersCardMoreActionMenu:storeTransfer.operationalHistoryNote')}
                </Text>
              </Stack>
            </Alert>

            {warnings.length > 0 ? (
              <Alert
                color="yellow"
                title={t('OrdersCardMoreActionMenu:storeTransfer.warningsTitle')}
              >
                <ul>
                  {warnings.map((warning) => (
                    <li key={`${warning.code}-${warning.message}`}>
                      {getTransferIssueMessage(warning)}
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={isPending}>
            {t('CommonButtons:cancel')}
          </Button>
          {!canConfirm ? (
            <Button
              onClick={handlePreview}
              loading={previewTransfer.isPending}
              disabled={
                !targetStoreId || destinationOptions.length === 0 || transferOrder.isPending
              }
            >
              {t('OrdersCardMoreActionMenu:storeTransfer.continue')}
            </Button>
          ) : (
            <Button
              onClick={handleTransfer}
              loading={transferOrder.isPending}
              disabled={
                !cashierBoxSelectionReady ||
                targetCashierBoxesQuery.isLoading ||
                targetCashierBoxesQuery.isError
              }
            >
              {t(
                warnings.length > 0
                  ? 'OrdersCardMoreActionMenu:storeTransfer.confirmWithWarnings'
                  : 'OrdersCardMoreActionMenu:storeTransfer.confirm'
              )}
            </Button>
          )}
        </Group>
      </Stack>
    </Drawer>
  );
};

export default OrderStoreTransferDrawer;
